import { Line, Param, SessionData } from '@/types/analytics';
import { Box, CircularProgress, Stack, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, JSX, useEffect, useState } from 'react';

type PromptsLogsProps = {
  sessionData?: SessionData;
  isLoading: boolean;
};

const PromptsLogs: FC<PromptsLogsProps> = ({ sessionData, isLoading }) => {
  const [promptsRows, setPromptsRows] = useState<JSX.Element[]>([]);

  const ignoredBlockTypes = ['PromptExist', 'SetPromptList', 'PromptDuration', 'SetConditional'];

  const parsePrompt = (prompt: Param) => {
    const files: { promptName: string; fileName: string }[] = [];

    const prefix = 'Promptlist:';
    if (!prompt.value.startsWith(prefix)) return;

    const trimmedValue = prompt.value.slice(prefix.length).trim();

    const splitValue = trimmedValue.split(']');
    splitValue.forEach((v) => {
      const value = v.substring(v.indexOf('Prompt'));
      const index = value.indexOf(' ');
      const contentType = value.slice(0, index);
      const promptValue = value.slice(index + 1);

      if (contentType === 'Prompt') {
        const startBracket = promptValue.indexOf('[');
        const promptContent = promptValue.slice(startBracket + 1);
        const split = promptContent.split(',');
        files.push({
          promptName: split[0].trim(),
          fileName: split[1].trim()
        });
      } else {
        const end = promptValue.indexOf(',');
        const promptContent = promptValue.slice(0, end);
        files.push({
          promptName: contentType,
          fileName: promptContent
        });
      }
    });

    return (
      <>
        <TableCell className='prompts-promptlist-name' width={400}>
          <Stack direction='column'>
            {files.map((file, index) => (
              <Box key={`prompt-${file.promptName}-${index}`}>{file.promptName}</Box>
            ))}
          </Stack>
        </TableCell>
        <TableCell className='prompts-promptlist-file' width={200}>
          <Stack direction='column'>
            {files.map((file, index) => (
              <Box key={`file-${file.fileName}-${index}`}>{file.fileName}</Box>
            ))}
          </Stack>
        </TableCell>
      </>
    );
  };

  const getPrompts = (line: Line) => {
    const foundPrompts: Param[] = [];
    sessionData?.params[line.step] &&
      Object.values(sessionData?.params[line.step]).forEach((param) => {
        if (param.parsertype === 'value+/(promptlist)' && param.value !== 'Promptlist: <empty>') {
          foundPrompts.push(param);
        }
      });

    if (!foundPrompts.length) return;

    return (
      <Fragment key={line.step}>
        {foundPrompts.map((file) => (
          <TableRow key={`${line.row}`} data-row={line.row} tabIndex={0}>
            <TableCell className='prompts-timestamp' width={1}>
              {line.timestamp}
            </TableCell>
            <TableCell className='prompts-step' width={1} sx={{ textAlign: 'right' }}>
              {line.step}
            </TableCell>
            <TableCell className='prompts-block-id' width={1} sx={{ textAlign: 'right' }}>
              {line.block_id}
            </TableCell>
            <TableCell className='prompts-channel' width={1}>
              {line.channel}
            </TableCell>
            <TableCell className='prompts-block-type' width={1}>
              {line.block_type}
            </TableCell>
            {parsePrompt(file)}
          </TableRow>
        ))}
      </Fragment>
    );
  };

  useEffect(() => {
    if (!sessionData) return;
    const newRows = sessionData.lines
      .map((line) => {
        if (!sessionData.params[line.step] || ignoredBlockTypes.includes(line.block_type)) return;
        return getPrompts(line);
      })
      .filter((row) => row !== undefined);

    setPromptsRows(newRows);
  }, [sessionData]);

  return (
    <Stack direction='column'>
      <Box height={'calc(100vh - 85px)'} overflow='auto'>
        {isLoading ? (
          <Stack alignItems='center' mt={10}>
            <CircularProgress color='inherit' />
          </Stack>
        ) : promptsRows.length > 0 ? (
          <Table size='small'>
            <TableBody>{promptsRows}</TableBody>
          </Table>
        ) : (
          <Stack alignItems='center' mt={10}>
            No Prompts found
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default PromptsLogs;
